/**
 * Small profile picture badge shown next to the username in the sidebar.
 * Click opens a modal to upload/replace/remove the picture.
 * Persisted in ZeeCore ObjectStore (S3 in prod) per org + user.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Upload, UserRound, X } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';

const LOCAL_CACHE_PREFIX = 'zecure_customer_logo:';
const MAX_FILE_BYTES = 1.5 * 1024 * 1024; // 1.5 MB
const DISPLAY_HEIGHT_PX = 28;

function localCacheKey(userKey: string): string {
  return `${LOCAL_CACHE_PREFIX}${userKey || 'default'}`;
}

function readLocalCache(userKey: string): string | null {
  try {
    return localStorage.getItem(localCacheKey(userKey));
  } catch {
    return null;
  }
}

function writeLocalCache(userKey: string, dataUrl: string | null): void {
  try {
    const key = localCacheKey(userKey);
    if (!dataUrl) localStorage.removeItem(key);
    else localStorage.setItem(key, dataUrl);
  } catch {
    // ignore quota / privacy mode failures
  }
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Could not read image'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read image'));
    reader.readAsDataURL(file);
  });
}

interface CustomerLogoBadgeProps {
  /** Prefer stable user id; email is fine as fallback (local cache key only). */
  userKey?: string | null;
}

export default function CustomerLogoBadge({ userKey }: CustomerLogoBadgeProps) {
  const cacheKey = (userKey || 'default').trim() || 'default';
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(() => readLocalCache(cacheKey));
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadFromBackend = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.branding.profilePicture());
      if (res.status === 404) {
        // One-time migrate from older localStorage-only uploads.
        const legacy = readLocalCache(cacheKey);
        if (legacy?.startsWith('data:')) {
          const putRes = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.branding.profilePicture(), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dataUrl: legacy }),
          });
          if (putRes.ok) {
            const body = await putRes.json() as { dataUrl?: string };
            const url = body.dataUrl || legacy;
            setLogoUrl(url);
            writeLocalCache(cacheKey, url);
            return;
          }
        }
        setLogoUrl(null);
        writeLocalCache(cacheKey, null);
        return;
      }
      if (!res.ok) throw new Error(`Profile picture load failed (${res.status})`);
      const body = await res.json() as { exists?: boolean; dataUrl?: string };
      if (body.exists && body.dataUrl) {
        setLogoUrl(body.dataUrl);
        writeLocalCache(cacheKey, body.dataUrl);
      } else {
        setLogoUrl(null);
        writeLocalCache(cacheKey, null);
      }
    } catch (err) {
      console.warn('[CustomerLogoBadge] Backend load failed; using local cache if any', err);
      setLogoUrl(readLocalCache(cacheKey));
    }
  }, [cacheKey]);

  useEffect(() => {
    setError(null);
    void loadFromBackend();
  }, [loadFromBackend]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalOpen]);

  const onFileSelected = useCallback(async (file: File | null) => {
    setError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError('Profile picture must be under 1.5 MB');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.branding.profilePicture(), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataUrl }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string; message?: string };
        throw new Error(body.error || body.message || `Upload failed (${res.status})`);
      }
      const body = await res.json() as { dataUrl?: string };
      const url = body.dataUrl || dataUrl;
      setLogoUrl(url);
      writeLocalCache(cacheKey, url);
      setModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload that image');
    } finally {
      setBusy(false);
    }
  }, [cacheKey]);

  const removePicture = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.branding.profilePicture(), {
        method: 'DELETE',
      });
      if (!res.ok && res.status !== 404) {
        throw new Error(`Remove failed (${res.status})`);
      }
      setLogoUrl(null);
      writeLocalCache(cacheKey, null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove profile picture');
    } finally {
      setBusy(false);
    }
  }, [cacheKey]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setError(null);
          setModalOpen(true);
        }}
        title="Change profile picture"
        aria-label="Change profile picture"
        className={`
          relative flex items-center justify-center shrink-0 rounded
          overflow-hidden cursor-pointer
          border border-border bg-background/60
          hover:border-muted-foreground transition
          focus:outline-none focus:ring-2 focus:ring-ring/60
        `}
        style={{ height: DISPLAY_HEIGHT_PX, width: DISPLAY_HEIGHT_PX }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Profile picture"
            className="h-full w-full object-contain p-0.5"
            draggable={false}
          />
        ) : (
          <UserRound className="w-3.5 h-3.5 text-muted-foreground" aria-hidden />
        )}
      </button>

      {modalOpen && (
        <div
          className="fixed inset-0 z-dropdown flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => !busy && setModalOpen(false)}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-profile-picture-title"
            className="w-full max-w-sm rounded-xl border border-border bg-background shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
              <h2
                id="change-profile-picture-title"
                className="text-base font-semibold text-white"
              >
                Change profile picture
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={busy}
                className="p-1 rounded-lg text-muted-foreground hover:text-white hover:bg-card transition disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4">
              <div className="flex justify-center">
                <div className="h-20 w-20 rounded-full border border-border bg-card/80 overflow-hidden flex items-center justify-center">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Current profile picture"
                      className="h-full w-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <UserRound className="w-8 h-8 text-muted-foreground" aria-hidden />
                  )}
                </div>
              </div>

              <label
                htmlFor={inputId}
                className={`flex flex-col items-center justify-center gap-2 w-full min-h-28 rounded-lg border border-dashed border-border bg-card/40 px-4 py-6 cursor-pointer hover:border-muted-foreground hover:bg-card/70 transition ${busy ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <Upload className="w-6 h-6 text-foreground" aria-hidden />
                <span className="text-sm font-medium text-white">
                  {busy ? 'Uploading…' : 'Upload from device'}
                </span>
                <span className="text-xs text-muted-foreground">PNG, JPEG, WebP, or SVG · max 1.5 MB</span>
                <input
                  ref={inputRef}
                  id={inputId}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                  className="sr-only"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    void onFileSelected(file);
                    e.target.value = '';
                  }}
                />
              </label>

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}

              {logoUrl && (
                <button
                  type="button"
                  onClick={() => void removePicture()}
                  disabled={busy}
                  className="w-full text-sm text-muted-foreground hover:text-destructive transition py-1 disabled:opacity-50"
                >
                  Remove profile picture
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
